import React, { FC, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import classNames from 'classnames/bind';

import { RootState } from 'redux/store';
import { setLogged, setUser } from 'redux/actionCreators/userActions';

import { logoutHandler } from 'utils/helpers/logout';
import { isObjectEmpty } from 'utils/helpers/functions';

import { UserDataType } from 'components/Header/Header.types';

import styles from 'components/Header/Header.module.scss';

const cn = classNames.bind(styles);

interface UserDropdownProps {
  isUserPopupActive: boolean;
  isMobile: boolean;
};

interface IHasSrc extends HTMLImageElement {
  src: string;
};

const UserDropdown: FC<UserDropdownProps> = ({ isUserPopupActive, isMobile }): React.ReactElement => {
  const router = useRouter();

  const dispatch = useDispatch();
  const { user, logged } = useSelector((state: RootState) => state.userData);
  const [{ user: userStore }, _, remove] = useCookies(['user']);

  const [userData, setUserState] = useState<UserDataType | null>(user || userStore);

  if (!logged) return <div></div>;
  if (isObjectEmpty(userData)) return <div></div>;

  const generateValidityDate = () => {
    if (userData?.validity) {
      return new Intl.DateTimeFormat('en-US', validityDateOptions).format(new Date(userData.validity));
    } else {
      return 'no data';
    }
  };

  const userProfileStatus = userData?.isAdmin ? 'admin' : 'member';
  const validityDateOptions = { year: 'numeric', month: 'short', day: 'numeric' } as const;
  const validityDate = generateValidityDate();

  const logout = () => {
    logoutHandler(() => {
      dispatch(setUser(null));
      dispatch(setLogged(false));
      remove('user');
      setUserState(null);
      router.push(location.pathname === '/directory' ? '/auth/sign-in' : '/');
    })
  }

  const logoImgErrorHandler = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const target = e.target as IHasSrc;
    if (target) {
      target.src = '/images/pages/Directory/user-logo-default.svg';
      console.clear();
    }
  };

  return (
    <div className={cn('user-dropdown', { 'active': isUserPopupActive, 'mobOpen': isMobile })}>
      <div className={cn('user-dropdown__header')}>
        <img
          className={cn('header__icon')}
          alt="DFA user icon"
          src={userData?.logo ?? '/images/pages/Directory/user-logo-default.svg'}
          onError={logoImgErrorHandler}
        />
        <div className={cn('header__context')}>
          <h4 className={cn('header__context-username')}>Hi {userData?.first_name || 'Unknown user'}!</h4>
          <p className={cn('header__context-company')}>{userData?.company || 'Support'}</p>
        </div>
      </div>
      <div className={cn('user-dropdown__context')}>
        {!userData?.isAdmin && (
          <a
            className={cn('user-dropdown__profile-line', 'user-dropdown__profile-status')}
            href="/directory/virtual-office"
          >
            Profile
            <span className={userProfileStatus}>{userProfileStatus}</span>
          </a>
        )}
        <button className={cn('user-dropdown__signout-btn')} onClick={logout}>
          Sign out
        </button>
      </div>
      {userData?.type === 'PREMIUM' ? (
        <div className={cn('user-dropdown__footer')}>
          <p className={cn('user-dropdown__expires-status')}>
            DFA Member Expires <span>{validityDate}</span>
          </p>
          {/* (!) New user registration disabled (by the https://searate.atlassian.net/browse/IMPACT-2774) */}
          {/* <a
            className={cn('user-dropdown__expires-btn')}
            href="/membership"
          >
            Renew membership
          </a> */}
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

export { UserDropdown };
